import * as React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { AutoSizer, List } from 'react-virtualized'

import DatePickerCell from '~/client/src/shared/components/DatePickerCell/DatePickerCell'
import { Separator } from '~/client/src/shared/components/shadcn-ui/separator'

import ProjectDateStore from '../../../stores/ui/ProjectDate.store'
import {
  MAX_MONTHS_BACK,
  MAX_MONTHS_FORWARD,
  YEARS_BACK,
  YEARS_FORWARD,
} from '../CommonDateFilter.constants'
import { CommonDateFilterMode } from '../CommonDateFilterMode'

interface IProps {
  currentMode: CommonDateFilterMode
  startDate: Date
  endDate: Date
  onMonthSelect: (date: Date) => void
  projectDateStore: ProjectDateStore
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const YEAR_ROW_HEIGHT = 180
const OVERSCAN_COUNT = 3

@observer
export default class MonthsPicker extends React.Component<IProps> {
  @observable private hoveredMonthValue: number | null = null

  private readonly baseYear: number
  private readonly totalYears: number
  private currentMonthValue = 0

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)

    const [currentYear] = props.projectDateStore.getYearMonthDay(new Date())
    this.baseYear = currentYear - YEARS_BACK
    this.totalYears = YEARS_BACK + YEARS_FORWARD + 1
  }

  public render() {
    const { projectDateStore } = this.props
    const [currentYear, currentMonth] = projectDateStore.getYearMonthDay(
      new Date(),
    )
    this.currentMonthValue = currentYear * 12 + (currentMonth - 1)

    const scrollToIndex = this.getInitialScrollIndex()

    return (
      <div className="tw:flex-1 tw:h-full tw:min-h-0">
        <AutoSizer>
          {({ width, height }) => (
            <List
              width={width}
              height={height}
              rowCount={this.totalYears}
              rowHeight={YEAR_ROW_HEIGHT}
              rowRenderer={this.renderYearRow}
              overscanRowCount={OVERSCAN_COUNT}
              scrollToIndex={scrollToIndex}
              scrollToAlignment="start"
            />
          )}
        </AutoSizer>
      </div>
    )
  }

  private getInitialScrollIndex(): number {
    const { projectDateStore, startDate } = this.props

    const target = startDate ?? new Date()
    const [year] = projectDateStore.getYearMonthDay(target)

    return year - this.baseYear
  }

  private renderYearRow = ({
    index,
    key,
    style,
  }: {
    index: number
    key: string
    style: React.CSSProperties
  }): JSX.Element => {
    const year = this.baseYear + index

    return (
      <div
        key={key}
        style={style}
        className="tw:px-4 tw:box-border"
        data-year={year}
      >
        <div className="tw:text-title-md tw:text-foreground tw:mb-2 tw:pt-2">
          {year}
        </div>
        <div className="tw:grid tw:grid-cols-4 tw:gap-y-2">
          {MONTH_NAMES.map((monthName, monthIndex) =>
            this.renderMonth(year, monthIndex, monthName),
          )}
        </div>
        <Separator className="tw:my-2.5" />
      </div>
    )
  }

  private renderMonth(
    year: number,
    monthIndex: number,
    monthName: string,
  ): JSX.Element {
    const { startDate, endDate, currentMode, projectDateStore } = this.props

    const [startYear, startMonth] = projectDateStore.getYearMonthDay(startDate)
    const [endYear, endMonth] = projectDateStore.getYearMonthDay(endDate)

    const cellValue = year * 12 + monthIndex
    const startValue = startYear * 12 + (startMonth - 1)
    const endValue = endYear * 12 + (endMonth - 1)

    const isCurrentMonth = cellValue === this.currentMonthValue
    const monthsFromToday = cellValue - this.currentMonthValue
    const isOutOfWindow =
      monthsFromToday < -MAX_MONTHS_BACK || monthsFromToday > MAX_MONTHS_FORWARD

    const isMonthMode = currentMode === CommonDateFilterMode.MONTH
    const isThreeMonthsMode = currentMode === CommonDateFilterMode.THREE_MONTHS

    const isRangeStart = cellValue === startValue
    const isRangeEnd = cellValue === endValue
    const isInRange = cellValue >= startValue && cellValue <= endValue
    const isMiddleMonth = isInRange && !isRangeStart && !isRangeEnd

    let isSelected = false
    let isInRangeMiddle = false
    let isSelectedStart = false
    let isSelectedEnd = false

    if (isMonthMode) {
      isSelected = isRangeStart
    } else if (isThreeMonthsMode) {
      isSelected = isRangeStart || isRangeEnd
      isInRangeMiddle = isMiddleMonth
      isSelectedStart = isRangeStart
      isSelectedEnd = isRangeEnd
    }

    let isHoverStart = false
    let isHoverMiddle = false
    let isHoverEnd = false
    if (isThreeMonthsMode && this.hoveredMonthValue !== null) {
      const hoverEnd = this.hoveredMonthValue + 2
      const isInHoverRange =
        cellValue >= this.hoveredMonthValue && cellValue <= hoverEnd

      if (isInHoverRange && !isSelected && !isInRangeMiddle) {
        isHoverStart = cellValue === this.hoveredMonthValue
        isHoverEnd = cellValue === hoverEnd
        isHoverMiddle = isInHoverRange && !isHoverStart && !isHoverEnd
      }
    }

    return (
      <DatePickerCell
        key={`${year}-${monthIndex}`}
        label={monthName}
        isSelected={isSelected}
        isRangeStart={isSelectedStart}
        isRangeEnd={isSelectedEnd}
        isInRange={isInRangeMiddle}
        isHoverStart={isHoverStart}
        isHoverMiddle={isHoverMiddle}
        isHoverEnd={isHoverEnd}
        isCurrent={isCurrentMonth}
        isDisabled={isOutOfWindow}
        onClick={() => this.handleMonthClick(year, monthIndex)}
        onMouseEnter={() => this.handleMonthHover(year, monthIndex)}
        onMouseLeave={this.handleMonthLeave}
      />
    )
  }

  private handleMonthClick = (year: number, monthIndex: number) => {
    const { projectDateStore, onMonthSelect } = this.props
    const monthStr = String(monthIndex + 1).padStart(2, '0')
    const isoDateString = `${year}-${monthStr}-01`
    const selectedDate = projectDateStore.fromIsoString(isoDateString)
    onMonthSelect(selectedDate)
  }

  @action
  private handleMonthHover = (year: number, monthIndex: number) => {
    const { currentMode } = this.props
    if (currentMode === CommonDateFilterMode.THREE_MONTHS) {
      this.hoveredMonthValue = year * 12 + monthIndex
    }
  }

  @action
  private handleMonthLeave = () => {
    this.hoveredMonthValue = null
  }
}
